import { SearchableSelect } from '@/components/SearchableSelect'
import type { CatalogVariation } from '@/lib/api/catalog/catalog-api'
import type { Dispatch, SetStateAction } from 'react'
import { MdAdd } from 'react-icons/md'
import { formatCentsLabel } from '../scheduling-format'
import type { ExamSelectedItem, OrderSelectedItem } from '../types'

type StackableItem = ExamSelectedItem | OrderSelectedItem

interface VariationRowControlsProps<T extends StackableItem> {
    item: T
    index: number
    items: T[]
    setItems: Dispatch<SetStateAction<T[]>>
    /** Variações disponíveis para o item do catálogo desta linha. */
    variations: CatalogVariation[]
    /**
     * Preço da variação pela cascata `parceiro > profissional > empresa`, com o
     * parceiro/profissional escolhidos no agendamento — igual ao backend.
     */
    priceOf: (variation: CatalogVariation) => number
    /** Menor preço de vínculo, para o "a partir de" quando não há preço próprio. */
    priceFromOf: (variation: CatalogVariation) => number | null
    /** Preço do item sem variação, usado ao limpar a escolha. */
    basePriceOf: (catalogId: number) => number
}

/**
 * Escolha de variação de uma linha empilhável (exame/procedimento).
 *
 * Cada variação vira uma linha própria — mesmo `catalogId`, `catalogVariationId`
 * distinto —, com dedupe por (item, variação): é o que permite pedir o mesmo
 * exame em duas variações sem que uma sobrescreva a outra.
 */
export default function VariationRowControls<T extends StackableItem>({
    item,
    index,
    items,
    setItems,
    variations,
    priceOf,
    priceFromOf,
    basePriceOf,
}: VariationRowControlsProps<T>) {
    if (variations.length === 0) return null

    const usedVariationIds = new Set(
        items
            .filter(
                other =>
                    other.catalogId === item.catalogId &&
                    other.catalogVariationId != null
            )
            .map(other => other.catalogVariationId)
    )
    const canAddMore = usedVariationIds.size < variations.length

    return (
        <div className="mt-1.5 flex flex-wrap items-center gap-2">
            <label className="text-xs text-base-content/60">Variação</label>
            {/* Combobox com busca: itens como "IgE, por alérgeno (cada)" têm
                169 variações — um <select> nativo é impraticável. */}
            <SearchableSelect
                className="w-72"
                searchThreshold={1}
                value={item.catalogVariationId ?? null}
                emptyLabel="Sem variação (preço base)"
                placeholder="Buscar variação..."
                options={variations.map(variation => {
                    const price = priceOf(variation)
                    const priceFrom = price > 0 ? null : priceFromOf(variation)
                    return {
                        value: variation.id,
                        label: variation.name,
                        /**
                         * Zero NÃO vira "R$ 0,00": a variação quase nunca tem
                         * preço próprio da empresa, e o zero se lê como "de
                         * graça" quando o certo é "ainda não definido".
                         *
                         * Havendo preço nos vínculos, mostra a ordem de
                         * grandeza; não havendo, o nome vai sozinho — o valor
                         * aparece quando o executante for escolhido.
                         */
                        hint:
                            price > 0
                                ? formatCentsLabel(price)
                                : priceFrom != null
                                  ? `a partir de ${formatCentsLabel(priceFrom)}`
                                  : undefined,
                        disabled:
                            usedVariationIds.has(variation.id) &&
                            variation.id !== item.catalogVariationId,
                    }
                })}
                onChange={val => {
                    const variationId = val == null ? undefined : (val as number)
                    const variation = variations.find(v => v.id === variationId)
                    setItems(prev =>
                        prev.map((it, idx) =>
                            idx === index
                                ? {
                                      ...it,
                                      catalogVariationId: variationId,
                                      variationName: variation?.name,
                                      unitPrice: variation
                                          ? priceOf(variation)
                                          : basePriceOf(it.catalogId),
                                  }
                                : it
                        )
                    )
                }}
            />
            {item.catalogVariationId != null && canAddMore && (
                <button
                    type="button"
                    className="btn btn-ghost btn-xs text-secondary gap-1"
                    title="Adicionar outra variação deste item"
                    onClick={() => {
                        const next = variations.find(
                            variation => !usedVariationIds.has(variation.id)
                        )
                        if (!next) return
                        setItems(prev => {
                            const copy = [...prev]
                            const newRow: T = {
                                ...item,
                                catalogVariationId: next.id,
                                variationName: next.name,
                                unitPrice: priceOf(next),
                            }
                            copy.splice(index + 1, 0, newRow)
                            return copy
                        })
                    }}
                >
                    <MdAdd /> Adicionar variação
                </button>
            )}
        </div>
    )
}
